import { TextField, Typography, Box, IconButton, InputAdornment, Paper, Button, MenuItem, Accordion, AccordionSummary, AccordionDetails, Alert } from '@mui/material';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import EntityDetailPopup from '../../utility/EntityDetailPopup';
import { popupClasses, EyeIcon, roomTablePopupClasses, AddEntityPlusIcon, metricPlanClasses, cardClasses, DownArrowIcon } from '../../utility';
import { useState, useCallback, useEffect, useContext } from 'react';
import CropRunFRTLPopup from '../../utility/popups/CropRunFRTLPopup';
import apiService from '../../../api/apiService';
import { FeedbackContext } from '../../../App';
import CheckIcon from '@mui/icons-material/Check';
import { FRTLContext } from '../../../context/FRTLContext';
import { getFrtlAliasString } from '../../../utility/frtlUtils';

export const growthPhaseOptionsForMetrc = [
  { value: 'Vegetative', label: 'Vegetative' },
  { value: 'Flowering', label: 'Flowering' },
];

export default function MetrcPlantEditPopup(props) {
  const { open, onClose, currDarkThemeClass, selectedAction, selectedFarmId, selectedPlants = [], onSave } = props;
  const feedbackCX = useContext(FeedbackContext);
  const { frtlContextValue, setFrtlContextValue } = useContext(FRTLContext);

  const [values, setValues] = useState({
    plants: [],
    activePopups: {},
    dropdownOptions: {
      wasteReasons: [],
      wasteMethods: [],
      units: [],
    },
    isLoadingOptions: false,
  });
  const [expandedPlants, setExpandedPlants] = useState({});
  const [templateValues, setTemplateValues] = useState({
    actual_date: '',
    // For change_location
    frtl: '',
    // For change_growth_phase
    new_growth_phase: '',
    // For destroy_plants
    waste_method_name: '',
    waste_material_mixed: '',
    waste_weight: '',
    waste_uom: '',
    waste_reason_name: '',
    count: 1,
    reason_note: '',
  });

  const fetchDestroyPlantsOptions = async () => {
    try {
      setValues(prev => ({ ...prev, isLoadingOptions: true }));
      const response = await apiService.fetchInstance(`new/destroy/?farm_id=${selectedFarmId}`);
      if (response?.data) {
        setValues(prev => ({
          ...prev,
          dropdownOptions: {
            wasteReasons: response.data.wastereason || [],
            wasteMethods: response.data.wastemethod || [],
            units: response.data.unit || [],
          },
          isLoadingOptions: false,
        }));
      }
    } catch (error) {
      console.error('Error fetching destroy plants options:', error);
      setValues(prev => ({ ...prev, isLoadingOptions: false }));
    }
  };

  const getValidationAlerts = () => {
    const alerts = [];

    if (selectedAction === 'change_location') {
      const sameLoc = values.plants.filter(plant => isSameLocation(plant));
      if (sameLoc.length > 0) {
        const plantTags = sameLoc.map(plant => plant.metrc_tag_number || 'No tag').join(', ');
        alerts.push({
          type: 'warning',
          message: `Plant${sameLoc.length > 1 ? 's' : ''} ${plantTags} ${
            sameLoc.length > 1 ? 'have' : 'has'
          } same current and new locations. Please update different locations.`,
          plantIndices: sameLoc.map(plant => values.plants.findIndex(p => p.id === plant.id)),
          tags: sameLoc.map(plant => plant.metrc_tag_number || 'No tag'),
        });
      }
    }

    if (selectedAction === 'change_growth_phase') {
      const samePhase = values.plants.filter(plant => isSameGrowthPhase(plant));
      if (samePhase.length > 0) {
        const plantTags = samePhase.map(plant => plant.metrc_tag_number || 'No tag').join(', ');
        alerts.push({
          type: 'warning',
          message: `Plant${samePhase.length > 1 ? 's' : ''} ${plantTags} ${
            samePhase.length > 1 ? 'have' : 'has'
          } same current and new growth phases. Please select different phases.`,
          plantIndices: samePhase.map(plant => values.plants.findIndex(p => p.id === plant.id)),
          tags: samePhase.map(plant => plant.metrc_tag_number || 'No tag'),
        });
      }
    }

    return alerts;
  };

  const handleTagClick = plantIndex => {
    setExpandedPlants(prev => ({
      ...prev,
      [plantIndex]: true,
    }));
  };

  useEffect(() => {
    if (selectedAction === 'destroy_plants') {
      fetchDestroyPlantsOptions();
    }
  }, [selectedAction]);

  useEffect(() => {
    if (selectedPlants.length > 0) {
      const plantsData = selectedPlants.map(plant => {
        const baseData = {
          id: plant.id,
          metrc_plant_id: plant.metrc_plant_id || '',
          metrc_tag_number: plant?.metrc_tag_number || '',
          actual_date: '',
        };

        if (selectedAction === 'change_location') {
          return {
            ...baseData,
            prev_frtl: plant?.frtl || '',
            frtl: '',
          };
        } else if (selectedAction === 'change_growth_phase') {
          return {
            ...baseData,
            current_growth_phase: plant?.plant_phase || '',
            new_growth_phase: '',
          };
        } else if (selectedAction === 'destroy_plants') {
          return {
            ...baseData,
            waste_method_name: '',
            waste_material_mixed: '',
            waste_weight: '',
            waste_uom: '',
            waste_reason_name: '',
            count: 1,
            reason_note: '',
          };
        }

        return baseData;
      });

      setValues(prev => ({
        ...prev,
        plants: plantsData,
      }));
    }
  }, [selectedPlants, selectedAction]);

  const handleSetTemplateValue = useCallback((prop, value) => {
    setTemplateValues(prev => ({
      ...prev,
      [prop]: value,
    }));
  }, []);

  const applyTemplateToAllPlants = () => {
    setValues(state => ({
      ...state,
      plants: state.plants.map(plant => {
        const updatedPlant = { ...plant };

        if (templateValues.actual_date) {
          updatedPlant.actual_date = templateValues.actual_date;
        }

        if (selectedAction === 'change_location') {
          if (templateValues.frtl) {
            updatedPlant.frtl = templateValues.frtl;
          }
        } else if (selectedAction === 'change_growth_phase') {
          if (templateValues.new_growth_phase) {
            updatedPlant.new_growth_phase = templateValues.new_growth_phase;
          }
        } else if (selectedAction === 'destroy_plants') {
          if (templateValues.waste_method_name) updatedPlant.waste_method_name = templateValues.waste_method_name;
          if (templateValues.waste_material_mixed) updatedPlant.waste_material_mixed = templateValues.waste_material_mixed;
          if (templateValues.waste_weight) updatedPlant.waste_weight = templateValues.waste_weight;
          if (templateValues.waste_uom) updatedPlant.waste_uom = templateValues.waste_uom;
          if (templateValues.waste_reason_name) updatedPlant.waste_reason_name = templateValues.waste_reason_name;
          if (templateValues.count) updatedPlant.count = templateValues.count;
          if (templateValues.reason_note) updatedPlant.reason_note = templateValues.reason_note;
        }

        return updatedPlant;
      }),
    }));
  };

  const isFormValid = () => {
    if (values.plants.length === 0) return false;

    return values.plants.every(plant => {
      if (!plant.actual_date) return false;

      if (selectedAction === 'change_location') {
        return plant.frtl && plant.prev_frtl && !isSameLocation(plant);
      } else if (selectedAction === 'change_growth_phase') {
        return plant.new_growth_phase && plant.current_growth_phase && !isSameGrowthPhase(plant);
      } else if (selectedAction === 'destroy_plants') {
        return plant.waste_method_name && plant.waste_reason_name && plant.waste_uom && plant.waste_weight && plant.count > 0;
      }

      return true;
    });
  };

  const handleSetPlantValue = useCallback((plantIndex, prop, value) => {
    setValues(state => ({
      ...state,
      plants: state.plants.map((plant, index) => (index === plantIndex ? { ...plant, [prop]: value } : plant)),
    }));
  }, []);

  const handleSetPopupState = useCallback((plantIndex, popupType, isOpen) => {
    setValues(state => ({
      ...state,
      activePopups: {
        ...state.activePopups,
        [`${plantIndex}_${popupType}`]: isOpen,
      },
    }));
  }, []);

  const handleOpenPrevFRTLPopup = plantIndex => handleSetPopupState(plantIndex, 'prevFrtl', true);
  const handleClosePrevFRTLPopup = plantIndex => handleSetPopupState(plantIndex, 'prevFrtl', false);
  const handleOpenFRTLPopup = plantIndex => handleSetPopupState(plantIndex, 'frtl', true);
  const handleCloseFRTLPopup = plantIndex => handleSetPopupState(plantIndex, 'frtl', false);

  const handleSave = async () => {
    try {
      let endpoint = '';
      let updateData = {};

      if (selectedAction === 'change_location') {
        endpoint = `cropplan-plants/operations/change-location?farm_id=${selectedFarmId}`;
        updateData = {
          plants: values.plants.map(plant => ({
            id: plant.id,
            metrc_plant_id: plant.metrc_plant_id,
            metrc_tag_number: plant.metrc_tag_number,
            prev_frtl: plant.prev_frtl,
            frtl: plant.frtl,
            actual_date: plant.actual_date,
          })),
        };
      } else if (selectedAction === 'change_growth_phase') {
        endpoint = `cropplan-plants/operations/change-growth-phase?farm_id=${selectedFarmId}`;
        updateData = {
          plants: values.plants.map(plant => ({
            id: plant.id,
            metrc_plant_id: plant.metrc_plant_id,
            metrc_tag_number: plant.metrc_tag_number,
            current_growth_phase: plant.current_growth_phase,
            new_growth_phase: plant.new_growth_phase,
            actual_date: plant.actual_date,
          })),
        };
      } else if (selectedAction === 'destroy_plants') {
        endpoint = `cropplan-plants/operations/destroy-plants?farm_id=${selectedFarmId}`;
        updateData = {
          plants: values.plants.map(plant => ({
            id: plant.id,
            metrc_plant_id: plant.metrc_plant_id,
            metrc_tag_number: plant.metrc_tag_number,
            waste_method_name: plant.waste_method_name,
            waste_material_mixed: plant.waste_material_mixed,
            waste_weight: plant.waste_weight,
            waste_uom: plant.waste_uom,
            waste_reason_name: plant.waste_reason_name,
            count: plant.count,
            reason_note: plant.reason_note,
            actual_date: plant.actual_date,
          })),
        };
      }

      const response = await apiService.modifyInstance(endpoint, updateData);

      if (response?.status === 200) {
        feedbackCX?.setContextValue(true, {
          type: 'success',
          message: response?.data?.message || 'Plants updated successfully!',
        });
      }

      if (onSave) {
        onSave(response);
      }

      onClose();
    } catch (error) {
      feedbackCX?.setContextValue(true, {
        type: 'error',
        message:
          Array.isArray(error?.response?.data?.error)
            ? error?.response?.data?.error[0]?.message || "Error updating Plants."
            : error?.response?.data?.error || "Error updating Plants.",
      });
      console.error('Error saving plant data:', error?.response.data?.error || error);
    }
  };

  const textFieldProp = ({ plantIndex, prop, inputProps = {}, otherTextProps = {} }) => ({
    autoComplete: 'off',
    className: `${popupClasses?.strainEntry?.textField} ${currDarkThemeClass}`,
    fullWidth: true,
    inputProps,
    label: '',
    onChange: ({ target: { value } }) => handleSetPlantValue(plantIndex, prop, value),
    value: values?.plants?.[plantIndex]?.[prop] ?? '',
    ...otherTextProps,
  });

  const getTemplateFieldProps = (prop, otherTextProps = {}) => ({
    autoComplete: 'off',
    className: `${popupClasses?.strainEntry?.textField} ${currDarkThemeClass}`,
    fullWidth: true,
    label: '',
    onChange: ({ target: { value } }) => handleSetTemplateValue(prop, value),
    value: templateValues[prop] ?? '',
    ...otherTextProps,
  });

  const getTemplateNewFrtlProps = () => ({
    otherTextProps: {
      label: 'New Location (FRTL) - Template',
      value: getFrtlAliasString(templateValues.frtl, frtlContextValue) ?? '',
      placeholder: 'FARM - ROOM - TABLE - LEVEL',
      variant: 'outlined',
      InputProps: {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={() => handleSetPopupState('template', 'frtl', true)}>
              <EyeIcon />
            </IconButton>
          </InputAdornment>
        ),
      },
    },
    inputProps: {
      disabled: true,
    },
  });

  const getTemplateFRTLPopupProps = () => ({
    open: values?.activePopups?.[`template_frtl`] || false,
    currDarkThemeClass,
    onClose: () => handleSetPopupState('template', 'frtl', false),
    handleEditFrtl: value => handleSetTemplateValue('frtl', value),
    selectedFarmId: selectedFarmId,
    farm: +templateValues?.frtl?.split('.')[0] ?? '',
    room: +templateValues?.frtl?.split('.')[1] ?? '',
    table: +templateValues?.frtl?.split('.')[2] ?? '',
    level: +templateValues?.frtl?.split('.')[3] ?? '',
  });

  const getTemplateActualDateProps = () => {
    return {
      label: 'Actual Date',
      type: 'date',
      variant: 'outlined',
      required: true,
      InputLabelProps: {
        shrink: true,
      },
    };
  };

  const getMetrcTagProps = plantIndex => ({
    plantIndex,
    prop: 'metrc_tag_number',
    otherTextProps: {
      label: 'Plant (Metrc Tag Number)',
      disabled: true,
      variant: 'outlined',
    },
  });

  const getActualDateProps = plantIndex => {
    return {
      plantIndex,
      prop: 'actual_date',
      otherTextProps: {
        label: 'Actual Date',
        type: 'date',
        variant: 'outlined',
        required: true,
        InputLabelProps: {
          shrink: true,
        },
      },
    };
  };

  const getWasteMethodProps = plantIndex => ({
    plantIndex,
    prop: 'waste_method_name',
    otherTextProps: {
      label: 'Waste Method',
      select: true,
      variant: 'outlined',
      required: true,
      disabled: values.isLoadingOptions,
    },
  });

  const getWasteReasonProps = plantIndex => ({
    plantIndex,
    prop: 'waste_reason_name',
    otherTextProps: {
      label: 'Waste Reason',
      select: true,
      variant: 'outlined',
      required: true,
      disabled: values.isLoadingOptions,
    },
  });

  const getWasteUomProps = plantIndex => ({
    plantIndex,
    prop: 'waste_uom',
    otherTextProps: {
      label: 'Unit of Measurement *',
      select: true,
      variant: 'outlined',
      required: true,
      disabled: values.isLoadingOptions,
    },
  });

  const getWasteMaterialMixedProps = plantIndex => ({
    plantIndex,
    prop: 'waste_material_mixed',
    otherTextProps: {
      label: 'Waste Material Mixed',
      variant: 'outlined',
      multiline: true,
      rows: 2,
    },
  });

  const getWasteWeightProps = plantIndex => ({
    plantIndex,
    prop: 'waste_weight',
    otherTextProps: {
      label: 'Waste Weight',
      variant: 'outlined',
      type: 'number',
      required: true,
      inputProps: { min: 0, step: 0.01 },
    },
  });

  const getCountProps = plantIndex => ({
    plantIndex,
    prop: 'count',
    otherTextProps: {
      label: 'Count',
      variant: 'outlined',
      type: 'number',
      required: true,
      inputProps: { min: 1, step: 1 },
    },
  });

  const getReasonNoteProps = plantIndex => ({
    plantIndex,
    prop: 'reason_note',
    otherTextProps: {
      label: 'Reason Note',
      variant: 'outlined',
      multiline: true,
      rows: 3,
    },
  });

  const getPrevFrtlProps = plantIndex => ({
    plantIndex,
    otherTextProps: {
      label: 'Current Location (FRTL) *',
      value: getFrtlAliasString(values?.plants?.[plantIndex]?.prev_frtl, frtlContextValue) ?? '',
      placeholder: 'FARM - ROOM - TABLE - LEVEL',
      disabled: true,
      variant: 'outlined',
      required: true,
      InputProps: {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={() => handleOpenPrevFRTLPopup(plantIndex)}>
              <EyeIcon />
            </IconButton>
          </InputAdornment>
        ),
      },
    },
    inputProps: {
      disabled: true,
    },
  });

  const getNewFrtlProps = plantIndex => ({
    plantIndex,
    otherTextProps: {
      label: 'New Location (FRTL) *',
      value: getFrtlAliasString(values?.plants?.[plantIndex]?.frtl, frtlContextValue) ?? '',
      placeholder: 'FARM - ROOM - TABLE - LEVEL',
      variant: 'outlined',
      required: true,
      InputProps: {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={() => handleOpenFRTLPopup(plantIndex)}>
              <EyeIcon />
            </IconButton>
          </InputAdornment>
        ),
      },
    },
    inputProps: {
      disabled: true,
    },
  });

  const getCurrentGrowthPhaseProps = plantIndex => ({
    plantIndex,
    prop: 'current_growth_phase',
    otherTextProps: {
      label: 'Current Growth Phase *',
      disabled: true,
      variant: 'outlined',
      required: true,
    },
  });

  const getNewGrowthPhaseDropdownProps = plantIndex => ({
    plantIndex,
    prop: 'new_growth_phase',
    otherTextProps: {
      label: 'New Growth Phase',
      select: true,
      variant: 'outlined',
      required: true,
    },
  });

  const getPrevFRTLPopupProps = plantIndex => ({
    open: values?.activePopups?.[`${plantIndex}_prevFrtl`] || false,
    currDarkThemeClass,
    onClose: () => handleClosePrevFRTLPopup(plantIndex),
    handleEditFrtl: () => {},
    selectedFarmId: selectedFarmId,
    farm: +values?.plants?.[plantIndex]?.prev_frtl?.split('.')[0] ?? '',
    room: +values?.plants?.[plantIndex]?.prev_frtl?.split('.')[1] ?? '',
    table: +values?.plants?.[plantIndex]?.prev_frtl?.split('.')[2] ?? '',
    level: +values?.plants?.[plantIndex]?.prev_frtl?.split('.')[3] ?? '',
    readOnly: true,
  });

  const getNewFRTLPopupProps = plantIndex => ({
    open: values?.activePopups?.[`${plantIndex}_frtl`] || false,
    currDarkThemeClass,
    onClose: () => handleCloseFRTLPopup(plantIndex),
    handleEditFrtl: value => handleSetPlantValue(plantIndex, 'frtl', value),
    selectedFarmId: selectedFarmId,
    farm: +values?.plants?.[plantIndex]?.frtl?.split('.')[0] ?? '',
    room: +values?.plants?.[plantIndex]?.frtl?.split('.')[1] ?? '',
    table: +values?.plants?.[plantIndex]?.frtl?.split('.')[2] ?? '',
    level: +values?.plants?.[plantIndex]?.frtl?.split('.')[3] ?? '',
  });

  const addNewPlant = () => {
    const baseNewPlant = {
      id: `temp_${Date.now()}`,
      metrc_tag_number: '',
      actual_date: '',
    };

    let newPlant = baseNewPlant;

    if (selectedAction === 'change_location') {
      newPlant = {
        ...baseNewPlant,
        prev_frtl: '',
        frtl: '',
      };
    } else if (selectedAction === 'change_growth_phase') {
      newPlant = {
        ...baseNewPlant,
        current_growth_phase: '',
        new_growth_phase: '',
      };
    } else if (selectedAction === 'destroy_plants') {
      newPlant = {
        ...baseNewPlant,
        waste_method_name: '',
        waste_material_mixed: '',
        waste_weight: '',
        waste_uom: '',
        waste_reason_name: '',
        count: 1,
        reason_note: '',
      };
    }

    setValues(prev => ({
      ...prev,
      plants: [...prev.plants, newPlant],
    }));
  };

  const removePlant = plantIndex => {
    setValues(prev => ({
      ...prev,
      plants: prev.plants.filter((_, index) => index !== plantIndex),
    }));

    setExpandedPlants(prev => {
      const newExpanded = {};
      Object.keys(prev).forEach(key => {
        const index = parseInt(key);
        if (index < plantIndex) {
          newExpanded[index] = prev[key];
        } else if (index > plantIndex) {
          newExpanded[index - 1] = prev[key];
        }
      });
      return newExpanded;
    });

    setValues(prevValues => ({
      ...prevValues,
      activePopups: Object.keys(prevValues.activePopups).reduce((acc, key) => {
        const [popupPlantIndex] = key.split('_');
        if (parseInt(popupPlantIndex) !== plantIndex) {
          acc[key] = prevValues.activePopups[key];
        }
        return acc;
      }, {}),
    }));
  };

  const renderClickableTags = (tags, plantIndices) => {
    return tags.map((tag, index) => (
      <span key={index}>
        <span
          onClick={() => handleTagClick(plantIndices[index])}
          style={{
            color: '#1976d2',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontWeight: 'bold',
          }}
          onMouseEnter={e => {
            e.target.style.backgroundColor = 'rgba(25, 118, 210, 0.1)';
            e.target.style.padding = '2px 4px';
            e.target.style.borderRadius = '4px';
          }}
          onMouseLeave={e => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.padding = '0';
          }}
        >
          {tag}
        </span>
        {index < tags.length - 1 && ', '}
      </span>
    ));
  };

  const renderTemplateLocationFields = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField {...getTemplateFieldProps('frtl', getTemplateNewFrtlProps().otherTextProps)} />
      <TextField {...getTemplateFieldProps('actual_date', getTemplateActualDateProps())} />
      {values?.activePopups?.[`template_frtl`] && <CropRunFRTLPopup {...getTemplateFRTLPopupProps()} />}
    </Box>
  );

  const renderTemplateGrowthPhaseFields = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField {...getTemplateFieldProps('new_growth_phase', getNewGrowthPhaseDropdownProps(0).otherTextProps)}>
        {growthPhaseOptionsForMetrc
          .filter(option => option.value !== 'Destroyed')
          .map((option, index) => (
            <MenuItem key={index} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
      </TextField>
      <TextField {...getTemplateFieldProps('actual_date', getTemplateActualDateProps())} />
    </Box>
  );

  const renderTemplateDestroyPlantsFields = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField {...getTemplateFieldProps('waste_method_name', getWasteMethodProps(0).otherTextProps)}>
          {values.dropdownOptions.wasteMethods.map(method => (
            <MenuItem key={method.id} value={method.name}>
              {method.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField {...getTemplateFieldProps('waste_reason_name', getWasteReasonProps(0).otherTextProps)}>
          {values.dropdownOptions.wasteReasons.map(reason => (
            <MenuItem key={reason.id} value={reason.name}>
              {reason.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField {...getTemplateFieldProps('waste_uom', getWasteUomProps(0).otherTextProps)}>
          {values.dropdownOptions.units
            .filter(unit => unit.unit_type === 'WeightBased')
            .map(unit => (
              <MenuItem key={unit.id} value={unit.unit_name}>
                {unit.unit_name} ({unit.unit_type})
              </MenuItem>
            ))}
        </TextField>
        <TextField {...getTemplateFieldProps('waste_weight', getWasteWeightProps(0).otherTextProps)} />
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField {...getTemplateFieldProps('count', getCountProps(0).otherTextProps)} />
        <TextField {...getTemplateFieldProps('actual_date', getTemplateActualDateProps())} />
      </Box>

      <TextField {...getTemplateFieldProps('waste_material_mixed', getWasteMaterialMixedProps(0).otherTextProps)} />
      <TextField {...getTemplateFieldProps('reason_note', getReasonNoteProps(0).otherTextProps)} />
    </Box>
  );

  const isSameLocation = plant => {
    return plant.prev_frtl && plant.frtl && plant.prev_frtl === plant.frtl;
  };

  const isSameGrowthPhase = plant => {
    return plant.current_growth_phase && plant.new_growth_phase && plant.current_growth_phase === plant.new_growth_phase;
  };

  const renderTemplateSection = () => {
    if (values.plants.length < 2) return null;

    const alerts = getValidationAlerts();

    return (
      <Paper className={`${metricPlanClasses?.metricRunPaperWrapper} ${currDarkThemeClass}`} style={{ marginBottom: '16px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" style={{ color: '#BDD959' }}>
            Template - Apply to All Plants
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<CheckIcon />}
            onClick={applyTemplateToAllPlants}
            style={{ minWidth: 'auto', padding: '6px 12px' }}
          >
            Apply to All
          </Button>
        </Box>

        {selectedAction === 'change_location' && renderTemplateLocationFields()}
        {selectedAction === 'change_growth_phase' && renderTemplateGrowthPhaseFields()}
        {selectedAction === 'destroy_plants' && renderTemplateDestroyPlantsFields()}

        {/* Show validation alerts here with clickable tags */}
        {alerts.map((alert, index) => (
          <Alert key={index} severity={alert.type} sx={{ mt: 2 }}>
            <span>
              Plant{alert.tags.length > 1 ? 's' : ''} {renderClickableTags(alert.tags, alert.plantIndices)} {alert.tags.length > 1 ? 'have' : 'has'}{' '}
              {selectedAction === 'change_location'
                ? 'same current and new locations. Please update different locations.'
                : 'same current and new growth phases. Please select different phases.'}
            </span>
          </Alert>
        ))}
      </Paper>
    );
  };

  const togglePlantAccordion = plantIndex => {
    setExpandedPlants(prev => ({
      ...prev,
      [plantIndex]: !prev[plantIndex],
    }));
  };

  const renderLocationFields = plantIndex => {
    return (
      <>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField {...textFieldProp(getPrevFrtlProps(plantIndex))} />
          <ChangeCircleIcon sx={{ color: 'primary.main', fontSize: 28, marginTop: 2 }} />
          <TextField {...textFieldProp(getNewFrtlProps(plantIndex))} />
        </Box>
        <TextField {...textFieldProp(getActualDateProps(plantIndex))} />
        {values?.activePopups?.[`${plantIndex}_prevFrtl`] && <CropRunFRTLPopup {...getPrevFRTLPopupProps(plantIndex)} />}
        {values?.activePopups?.[`${plantIndex}_frtl`] && <CropRunFRTLPopup {...getNewFRTLPopupProps(plantIndex)} />}
      </>
    );
  };

  const renderGrowthPhaseFields = plantIndex => {
    return (
      <>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField {...textFieldProp(getCurrentGrowthPhaseProps(plantIndex))}>
            {growthPhaseOptionsForMetrc.map((option, index) => (
              <MenuItem key={index} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <ChangeCircleIcon sx={{ color: 'primary.main', fontSize: 28, marginTop: 2 }} />
          <TextField {...textFieldProp(getNewGrowthPhaseDropdownProps(plantIndex))}>
            {growthPhaseOptionsForMetrc
              .filter(option => option.value !== values?.plants?.[plantIndex]?.current_growth_phase && option.value !== 'Destroyed')
              .map((option, index) => (
                <MenuItem key={index} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
          </TextField>
        </Box>
        <TextField {...textFieldProp(getActualDateProps(plantIndex))} />
      </>
    );
  };

  const renderDestroyPlantsFields = plantIndex => (
    <>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField {...textFieldProp(getWasteMethodProps(plantIndex))}>
          {values.dropdownOptions.wasteMethods.map(method => (
            <MenuItem key={method.id} value={method.name}>
              {method.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField {...textFieldProp(getWasteReasonProps(plantIndex))}>
          {values.dropdownOptions.wasteReasons.map(reason => (
            <MenuItem key={reason.id} value={reason.name}>
              {reason.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField {...textFieldProp(getWasteUomProps(plantIndex))}>
          {values.dropdownOptions.units
            .filter(unit => unit.unit_type === 'WeightBased')
            .map(unit => (
              <MenuItem key={unit.id} value={unit.unit_name}>
                {unit.unit_name} ({unit.unit_type})
              </MenuItem>
            ))}
        </TextField>
        <TextField {...textFieldProp(getWasteWeightProps(plantIndex))} />
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField {...textFieldProp(getCountProps(plantIndex))} />
        <TextField {...textFieldProp(getActualDateProps(plantIndex))} />
      </Box>

      <TextField {...textFieldProp(getWasteMaterialMixedProps(plantIndex))} />

      <TextField {...textFieldProp(getReasonNoteProps(plantIndex))} />
    </>
  );

  const renderPlantPaper = (plant, plantIndex) => {
    const plantAccordionProps = {
      className: `${cardClasses.strainAccordion?.accordion} ${currDarkThemeClass}`,
      elevation: 0,
      expanded: expandedPlants[plantIndex] || false,
      onChange: () => togglePlantAccordion(plantIndex),
      square: true,
    };

    const plantAccordionSummaryProps = {
      className: `${cardClasses?.strainAccordion?.accordionSummary} ${currDarkThemeClass}`,
      expandIcon: <DownArrowIcon className={`${cardClasses?.strainAccordion.accordionExpandIcon} ${currDarkThemeClass}`} />,
    };

    return (
      <Accordion key={plantIndex} {...plantAccordionProps}>
        <AccordionSummary {...plantAccordionSummaryProps}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, width: '100%' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Plant #{plantIndex + 1} - {plant.metrc_tag_number || 'No tag number'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
              {values.plants.length > 1 && (
                <Button
                  size="small"
                  color="error"
                  onClick={e => {
                    e.stopPropagation();
                    removePlant(plantIndex);
                  }}
                  sx={{ minWidth: 'auto', fontSize: '12px' }}
                >
                  Remove
                </Button>
              )}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <TextField {...textFieldProp(getMetrcTagProps(plantIndex))} />
            {selectedAction === 'change_location' && renderLocationFields(plantIndex)}
            {selectedAction === 'change_growth_phase' && renderGrowthPhaseFields(plantIndex)}
            {selectedAction === 'destroy_plants' && renderDestroyPlantsFields(plantIndex)}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderActionContent = () => {
    if (selectedPlants.length === 0) {
      return (
        <Box>
          <Typography variant="body2" color="textSecondary">
            No plants selected for {selectedAction?.toLowerCase()}.
          </Typography>
        </Box>
      );
    }

    if (selectedAction === 'destroy_plants' && values.isLoadingOptions) {
      return (
        <Box>
          <Typography variant="body2" color="textSecondary">
            Loading options...
          </Typography>
        </Box>
      );
    }

    const rTAddLevelBtnIconProps = {
      className: `${roomTablePopupClasses?.addLevelBtnIcon} ${currDarkThemeClass}`,
    };

    const rTAddLevelBtnProps = {
      children: 'Add a Plant',
      className: `${roomTablePopupClasses?.addLevelBtn} ${currDarkThemeClass}`,
      fullWidth: true,
      startIcon: <AddEntityPlusIcon {...rTAddLevelBtnIconProps} />,
      onClick: addNewPlant,
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" color="textSecondary">
          {values.plants.length} plant{values.plants.length > 1 ? 's' : ''} selected for {selectedAction?.toLowerCase()}
        </Typography>

        {renderTemplateSection()}

        <Paper className={`${metricPlanClasses?.metricRunPaperWrapper} ${currDarkThemeClass}`}>
          {values.plants.map((plant, plantIndex) => renderPlantPaper(plant, plantIndex))}
        </Paper>
      </Box>
    );
  };

  const getTitle = () => {
    switch (selectedAction) {
      case 'change_location':
        return 'Change Plant Location';
      case 'change_growth_phase':
        return 'Change Plant Growth Phase';
      case 'destroy_plants':
        return 'Destroy Plants';
      default:
        return selectedAction || 'Plant Action';
    }
  };

  const getHeadContentText = () => {
    switch (selectedAction) {
      case 'change_location':
        return 'This allows you to change the location of selected plants directly on Metrc as well as update your records.';
      case 'change_growth_phase':
        return 'This allows you to change the growth phase of selected plants directly on Metrc as well as update your records.';
      case 'destroy_plants':
        return 'This allows you to destroy selected plants directly on Metrc as well as update your records with waste tracking information.';
      default:
        return 'This allows you to perform actions on selected plants directly on Metrc as well as wherever needed for you.';
    }
  };

  const entityDetailPopupProps = {
    currDarkThemeClass,
    open: open,
    onClose: onClose,
    title: getTitle(),
    headContentText: getHeadContentText(),
    onSaveClick: handleSave,
    toDisableSave: !isFormValid() || values.isLoadingOptions,
  };

  return <EntityDetailPopup {...entityDetailPopupProps}>{renderActionContent()}</EntityDetailPopup>;
}
